import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, InputAdornment, Button, Divider } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';
import { useDossieService } from '../../../../use-cases/dossie';

export const DossieSearchPage = () => {
  const [documento, setDocumento] = useState('');
  const [loading, setLoading] = useState(false);
  const { consultarDossie, fecharOrigemConsulta } = useDossieService();

  useEffect(() => {
    fecharOrigemConsulta();
  }, [fecharOrigemConsulta]);

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    let isAlphanumeric = /[A-Z]/.test(raw);

    if (!isAlphanumeric && raw.length <= 11) {
      // CPF: NNN.NNN.NNN-NN
      let masked = '';
      if (raw.length > 0) masked += raw.substring(0, 3);
      if (raw.length > 3) masked += '.' + raw.substring(3, 6);
      if (raw.length > 6) masked += '.' + raw.substring(6, 9);
      if (raw.length > 9) masked += '-' + raw.substring(9, 11);
      setDocumento(masked);
    } else {
      // CNPJ: AA.AAA.AAA/AAAA-NN
      // DV (last 2 characters) can only be numbers.
      let base = raw.substring(0, 12);
      let dv = raw.substring(12, 14).replace(/[^0-9]/g, '');
      let cnpjRaw = base + dv;

      let masked = '';
      if (cnpjRaw.length > 0) masked += cnpjRaw.substring(0, 2);
      if (cnpjRaw.length > 2) masked += '.' + cnpjRaw.substring(2, 5);
      if (cnpjRaw.length > 5) masked += '.' + cnpjRaw.substring(5, 8);
      if (cnpjRaw.length > 8) masked += '/' + cnpjRaw.substring(8, 12);
      if (cnpjRaw.length > 12) masked += '-' + cnpjRaw.substring(12, 14);
      setDocumento(masked);
    }
  };

  const handleSearch = () => {
    const cleanDoc = documento.replace(/[^A-Za-z0-9]/g, '');
    if (cleanDoc.length < 11) return;
    setLoading(true);
    fecharOrigemConsulta();
    consultarDossie(cleanDoc);
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        background: 'radial-gradient(circle at 50% -20%, #ffffff 0%, #f0ebf5 100%)',
        padding: 2,
        flexGrow: 1
      }}
    >
      <Box 
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 24px 48px -12px rgba(114, 57, 137, 0.25), 0 4px 24px rgba(0,0,0,0.04)',
          border: '1px solid rgba(255,255,255,0.8)',
          padding: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '6px',
            backgroundColor: 'primary.main',
          },
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
          transition: 'transform 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-5px)'
          }
        }}
      >
        {/* Título Principal */}
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', mb: 1, letterSpacing: '-0.5px' }}>
          Consultar{' '}
          <Box component="span" sx={{
            background: 'linear-gradient(135deg, #723989 0%, #a658c9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block'
          }}>
            Dossiê
          </Box>
        </Typography>
        
        {/* Subtítulo */}
        <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
          Insira o CPF ou CNPJ alvo para iniciar a análise de risco completa.
        </Typography>

        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            handleSearch();
          }}
          noValidate
        >
          {/* Input de Pesquisa */}
          <TextField
            fullWidth
            placeholder="000.000.000-00"
            value={documento}
            onChange={handleDocumentChange}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
            variant="outlined"
            sx={{
              mb: 4,
              '& input:-webkit-autofill': {
                transition: 'background-color 5000s ease-in-out 0s',
                WebkitTextFillColor: '#1e293b !important',
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                transition: 'all 0.2s ease-in-out',
                '& fieldset': { borderColor: '#e2e8f0', borderWidth: '2px' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#723989' },
                '&.Mui-focused': {
                  backgroundColor: '#ffffff',
                  boxShadow: '0 0 0 4px rgba(114, 57, 137, 0.15)'
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#68457c', ml: 1 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Botão de Ação Único */}
          <Box display="flex" justifyContent="center" mb={4}>
            <Button
              type="submit"
              fullWidth
              disabled={documento.replace(/[^A-Za-z0-9]/g, '').length < 11} // Bloqueia se não tiver ao menos um CPF
              sx={{
                background: 'linear-gradient(135deg, #723989 0%, #9c4dcc 100%)',
                color: '#fff',
                borderRadius: '16px',
                padding: '14px',
                fontSize: '1.05rem',
                textTransform: 'none',
                fontWeight: 700,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1.5,
                boxShadow: '0 8px 16px rgba(114, 57, 137, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #5c2d6e 0%, #8340ab 100%)',
                  boxShadow: '0 12px 24px rgba(114, 57, 137, 0.4)',
                  transform: 'translateY(-2px)'
                },
                '&:disabled': {
                  background: '#e0e0e0',
                  color: '#9e9e9e',
                  boxShadow: 'none'
                }
              }}
            >
              Realizar Consulta
              <span style={{ fontSize: '1.2rem', transition: 'transform 0.3s ease', display: 'inline-block', transform: documento.replace(/[^A-Za-z0-9]/g, '').length >= 11 ? 'translateX(4px)' : 'none' }}>→</span>
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Footer info */}
        <Box display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <LockIcon sx={{ fontSize: 14, color: '#e0a800' }} />
            <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.7rem' }}>
              Dados protegidos pela LGPD
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
